import React from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';

interface ConfigDetail {
  label: string;
  value: string;
}

interface ReadOnlyConfigCardProps {
  title: string;
  configured: boolean;
  details?: string;
  configDetails?: ConfigDetail[];
}

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  details: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    marginTop: 2,
  },
  configGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '2px 12px',
    marginTop: 6,
    fontSize: '0.8rem',
  },
  configLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
  },
  configValue: {
    color: theme.palette.text.primary,
    fontSize: '0.8rem',
    fontFamily: 'monospace',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const ReadOnlyConfigCard: React.FC<ReadOnlyConfigCardProps> = ({ title, configured, details, configDetails }) => {
  const classes = useStyles();

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="body1">{title}</Typography>
        <Chip
          size="small"
          icon={configured ? <CheckCircleIcon /> : <CancelIcon />}
          label={configured ? 'Configured' : 'Not configured'}
          color={configured ? 'primary' : 'default'}
          variant="outlined"
        />
      </Box>
      {details && !configDetails?.length && <Typography className={classes.details}>{details}</Typography>}
      {configDetails && configDetails.length > 0 && (
        <Box className={classes.configGrid}>
          {configDetails.map((d, i) => (
            <React.Fragment key={i}>
              <Typography className={classes.configLabel}>{d.label}:</Typography>
              <Typography className={classes.configValue}>{d.value}</Typography>
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ReadOnlyConfigCard;
